import React, { useEffect, useState } from 'react'
import { ClipboardCheck } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { CardContent, CardHeader, CardTitle, CardDescription } from './ui/card'
import { EmptyState } from './ui/empty-state'
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar'
import { Skeleton } from './ui/skeleton'
import { rangeLabelAsOf } from '../lib/dayLabel'

// ============================================================================
// Component: NeedsAttentionCard
//
// Real version of syncsimple-v2's "Needs your attention" widget — people who
// reported a blocker in the human standup (standup_responses.questions
// .blockers). Not gated: blocker text is already visible to any team member
// via the Standups page, so this doesn't need admin-only treatment.
//
// 2026-09-17: takes the dashboard's full `range` ({from, to} date-key
// strings) instead of a single `date` — filters standup_responses.date with
// `.gte`/`.lte` so a multi-day selection surfaces blockers from the whole
// window, not just its last day. A person can report a blocker on more than
// one day in a wide range; rather than list them once per day (which would
// make the same avatar repeat down the card), this dedupes to that person's
// MOST RECENT blocker in the range — same one-row-per-person shape as
// before, just keyed off "most recent in range" instead of "today's."
// ============================================================================

interface NeedsAttentionCardProps {
  teamId: string
  range: { from: string; to: string } // YYYY-MM-DD
  // Pod filter (NewDashboard.tsx's pod dropdown). Blocker text is already
  // visible to any team member (see the "Not gated" note above), so this is
  // just a `standup_responses` filter -- no masking concern, safe for any
  // role.
  userIds?: string[]
}

interface BlockerRow {
  userId: string
  name: string
  avatarUrl: string | null
  blocker: string
}

const NeedsAttentionCard: React.FC<NeedsAttentionCardProps> = ({ teamId, range, userIds }) => {
  const [rows, setRows] = useState<BlockerRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)

      // Empty (but non-null) userIds means "pod has no members" -- handled
      // explicitly rather than sent as `.in('user_id', [])`.
      if (userIds && userIds.length === 0) {
        setRows([])
        setLoading(false)
        return
      }

      let standupsQuery = supabase
        .from('standup_responses')
        .select('user_id, questions, date')
        .eq('team_id', teamId)
        .gte('date', range.from)
        .lte('date', range.to)
      if (userIds) standupsQuery = standupsQuery.in('user_id', userIds)

      const [standupsResult, usersResult] = await Promise.all([
        standupsQuery,
        supabase.from('users').select('id, name, email, avatar_url').eq('team_id', teamId),
      ])

      if (cancelled) return

      if (standupsResult.error || usersResult.error) {
        console.error('Failed to load blockers:', standupsResult.error ?? usersResult.error)
        setError('Could not load blockers.')
        setLoading(false)
        return
      }

      const usersById = new Map<
        string,
        { name: string | null; email: string | null; avatar_url: string | null }
      >(
        (
          (usersResult.data ?? []) as {
            id: string
            name: string | null
            email: string | null
            avatar_url: string | null
          }[]
        ).map((u) => [u.id, u])
      )

      // Keyed by user_id, keeping only the highest `date` seen per person so
      // a blocker reported earlier in the range never overwrites one from
      // later — see the file header comment for why this dedupes at all.
      const latestByUser = new Map<string, { date: string; blocker: string }>()
      for (const s of (standupsResult.data ?? []) as {
        user_id: string | null
        questions: { blockers?: string } | null
        date: string
      }[]) {
        const blocker = s.questions?.blockers
        if (!blocker || blocker === 'None' || !s.user_id) continue
        const existing = latestByUser.get(s.user_id)
        if (!existing || s.date > existing.date) {
          latestByUser.set(s.user_id, { date: s.date, blocker })
        }
      }

      const blockers: BlockerRow[] = Array.from(latestByUser.entries()).map(([userId, { blocker }]) => {
        const user = usersById.get(userId)
        return {
          userId,
          name: user?.name || user?.email?.split('@')[0] || 'Unknown user',
          avatarUrl: user?.avatar_url ?? null,
          blocker,
        }
      })

      setRows(blockers)
      setLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [teamId, range.from, range.to, userIds?.join(",")])

  return (
    <div className="flex h-full flex-col">
      <CardHeader>
        <CardTitle className="text-base">Needs your attention</CardTitle>
        <CardDescription>Blockers reported {rangeLabelAsOf(range.from, range.to)}</CardDescription>
      </CardHeader>
      <div className="border-t border-border" />
      {/* p-0 + its own divide-y/border-y list, matching PeopleAndAgentsPreview's
          sibling widget -- a row stack with space-y and no dividers read as
          inconsistent with every other list in the app (UI_STYLE_GUIDE.md §3). */}
      <CardContent className="flex flex-1 flex-col p-0">
        {loading ? (
          <div className="divide-y divide-border border-y border-border">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 px-6 py-4">
                <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-40" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <p className="px-6 pb-6 text-sm text-destructive">{error}</p>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={ClipboardCheck}
            message={`No blockers reported ${rangeLabelAsOf(range.from, range.to)}.`}
          />
        ) : (
          <div className="divide-y divide-border border-y border-border">
            {rows.map((row) => (
              <div key={row.userId} className="flex items-center gap-3 px-6 py-4">
                <Avatar>
                  {row.avatarUrl && <AvatarImage src={row.avatarUrl} alt="" />}
                  <AvatarFallback>{row.name.charAt(0).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">{row.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{row.blocker}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </div>
  )
}

export default NeedsAttentionCard
